/* eslint-disable @typescript-eslint/only-throw-error -- custom solution & refactor candidate */
import { CardNumberElement } from '@stripe/react-stripe-js';
import { type Stripe, type StripeElements } from '@stripe/stripe-js';
import Cookies from 'js-cookie';

import { sanitizeUrl } from '../sanitizeUrl';
import {
  sendPaymentIntentConfirmRequest,
  sendPaymentIntentRequest,
  sendPaymentSetupIntentRequest,
} from '../api/payment';
import { type RethrowableStripeError } from './errorHandlers';
import { createErrorWithCause } from '@/utils/createErrorWithCause';
import { paramsKeys } from '@/utils/consts/params';
export const paymentMethodTypes = {
  card: 'card',
  applePay: 'apple_pay',
  googlePay: 'google_pay',
  unknown: 'unknown_method',
} as const;
export type OneOfPaymentMethods = (typeof paymentMethodTypes)[keyof typeof paymentMethodTypes];

// https://stripe.com/docs/js/appendix/payment_response

export const paymentRequestCompleteStatuses = {
  success: 'success',
  fail: 'fail',
  invalidName: 'invalid_payer_name',
  invalidPhone: 'invalid_payer_phone',
  invalidEmail: 'invalid_payer_email',
  invalidAddress: 'invalid_shipping_address',
} as const;

type OneOfPaymentRequestSuccessCodes =
  (typeof paymentRequestCompleteStatuses)[keyof typeof paymentRequestCompleteStatuses];
export type OnCompleteCallback = (status?: OneOfPaymentRequestSuccessCodes) => void;

export interface BillingInfo {
  postalCode?: string;
  country?: string;
  state?: string;
}

export type RequiredBillingInfo = Pick<BillingInfo, 'state'> & Required<Omit<BillingInfo, 'state'>>;

const createPaymentMethod = (stripe: Stripe, elements: StripeElements, email: string, billingInfo?: BillingInfo) => {
  const cardElement = elements.getElement(CardNumberElement);

  if (!cardElement) {
    throw new Error('CardElement is not available');
  }

  return stripe
    .createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        email,
        address: {
          postal_code: billingInfo?.postalCode,
          country: billingInfo?.country,
          state: billingInfo?.state,
        },
      },
    })
    .then((res) => {
      if (res.error) throw res;

      return res.paymentMethod.id; // has a format of 'pm_1Q0PsIJvEtkwdCNYMSaVuRz6'
    });
};

const createPaymentIntent = (paymentMethodId: string, email: string, productId: string, coupon?: string) =>
  sendPaymentIntentRequest(paymentMethodId, email, productId, coupon).then((res) => {
    const {
      requires_action: isActionRequired,
      stripe_payment_intent_client_secret: intentClientSecret,
      stripe_payment_intent_id: paymentIntentId,
    } = res.data;

    return {
      isActionRequired,
      intentClientSecret,
      paymentIntentId,
      paymentMethodId,
    };
  });

const createPaymentIntentSetup = (paymentMethodId: string, email: string, productId: string, coupon?: string) =>
  sendPaymentSetupIntentRequest(paymentMethodId, email, productId, coupon).then((res) => {
    const {
      requires_action: isActionRequired,
      stripe_payment_setup_intent_client_secret: intentClientSecret,
      stripe_payment_setup_intent_id: paymentIntentId,
    } = res.data;

    return {
      isActionRequired,
      intentClientSecret,
      paymentIntentId,
      paymentMethodId,
    };
  });

const handleCardAction = (
  stripe: Stripe,
  intentClientSecret: string,
  handleErrorCardAction: (res: RethrowableStripeError) => never,
) =>
  stripe
    .handleCardAction(intentClientSecret)
    .then((res) => {
      if (res.error) throw res.error;

      return sendPaymentIntentConfirmRequest(res.paymentIntent.id);
    })
    .catch(handleErrorCardAction)
    .then(
      (res) => {
        if (!res.data.success) {
          throw new Error('Intent confirmation failed');
        }
      },
      (err: unknown) => {
        throw createErrorWithCause('Intent confirmation failed', err);
      },
    );

const handleSetupAction = (
  stripe: Stripe,
  intentSetupSecret: string,
  paymentMethodId: string,
  handleErrorCardAction: (res: RethrowableStripeError) => never,
): Promise<string> =>
  stripe
    .confirmCardSetup(intentSetupSecret, {
      payment_method: paymentMethodId,
    })
    .then((res) => {
      if (res.error) throw res.error;

      return res.setupIntent.id;
    })
    .catch(handleErrorCardAction);

const metadataKeys = [
  'funnel',
  'clickid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'ad_set',
  'shortcut',
  'adgroupid',
] as const;

const prepareSubscriptionMetadata = (): Record<string, string> => {
  const metadata: Record<string, string> = {};
  const params = new URLSearchParams(window.location.search);

  metadataKeys.forEach((key) => {
    const value = params.get(key);

    if (value) metadata[key] = value;
  });

  const startUrl = sanitizeUrl(Cookies.get('start_url'));

  if (startUrl) metadata.url = startUrl;

  const alternativeClickId = params.get(paramsKeys.IR_CLICK_ID);

  if (!metadata.clickid && alternativeClickId) {
    metadata.clickid = alternativeClickId;
  }

  const partnerstackClickId =
    params.get(paramsKeys.PARTNERSTACK_CLICK_ID) || params.get(paramsKeys.PARTNERSTACK_CLICK_ID_ALT);

  if (!metadata.clickid && partnerstackClickId) {
    metadata.clickid = partnerstackClickId;
  }

  const partnerstackPartnerKey = params.get(paramsKeys.PARTNERSTACK_PARTNER_KEY);

  if (partnerstackPartnerKey) {
    metadata.ps_partner_key = partnerstackPartnerKey;
  }

  return metadata;
};

const prepateSubscriptioSuccessUrl = (): string => {
  const params = new URLSearchParams(window.location.search);

  return sanitizeUrl(params.get('success_redirect_url')) || `${process.env.cabinetUrl}/payment/success`;
};

export {
  createPaymentMethod,
  createPaymentIntent,
  createPaymentIntentSetup,
  handleCardAction,
  handleSetupAction,
  prepareSubscriptionMetadata,
  prepateSubscriptioSuccessUrl,
};
